const list = (value) => Array.isArray(value) ? value : value == null ? [] : [value];

const ids = (value) => list(value)
  .map((item) => typeof item === "object" ? item?.id : item)
  .filter(Boolean)
  .map(String);

const normalizedDayMap = (value, mapper = (item) => item) => {
  if (!value || typeof value !== "object") return {};
  return Object.fromEntries(Object.entries(value).map(([day, item]) => [String(day), mapper(item)]));
};

export function registrationToFormOptions(registration = {}) {
  const participation = registration?.participation || {};
  const lodging = registration?.lodging || {};
  const meals = registration?.meals || {};
  const globalLodging = lodging?.mode === "global" ? lodging?.global || {} : {};
  const byDayLodging = lodging?.mode === "byDay" ? lodging?.byDay || {} : {};
  const byDayUnits = lodging?.byDayUnits || {};

  return {
    useFullEvent: participation?.isFullEvent !== false,
    daysSelected: list(participation?.daysSelected).map(Number).filter(Number.isInteger),
    selectedGlobalLodgingId: globalLodging?.id ? String(globalLodging.id) : "none",
    globalLodgingCheckIn: String(globalLodging?.checkIn || ""),
    globalLodgingCheckOut: String(globalLodging?.checkOut || ""),
    globalLodgingUnitChoice: String(globalLodging?.lodgingUnitId || ""),
    dayLodgingChoice: normalizedDayMap(byDayLodging, (item) =>
      String(typeof item === "object" ? item?.id || "" : item || "")
    ),
    dayLodgingUnitChoice: normalizedDayMap(byDayUnits, (item) => String(item || "")),
    selectedGlobalMealId: meals?.mode === "global" ? ids(meals?.global) : [],
    dayMealChoice: meals?.mode === "byDay"
      ? normalizedDayMap(meals?.byDay, (item) => ids(item))
      : {},
    dayWalkSlotChoice: normalizedDayMap(participation?.dayWalkSlotChoice, (item) => String(item || "")),
    includeTransfer: registration?.transfer?.enabled === true,
    selectedTransferPointId: String(registration?.transfer?.point?.id || registration?.transferPoint?.id || ""),
  };
}

const isSubset = (previous, next) => {
  const nextSet = new Set(list(next).map(String));
  return list(previous).map(String).every((item) => nextSet.has(item));
};

export function validatePaidRegistrationAdditions(previous = {}, next = {}) {
  const errors = [];
  const oldParticipation = previous?.participation || {};
  const newParticipation = next?.participation || {};

  if (oldParticipation.isFullEvent === true && newParticipation.isFullEvent !== true) {
    errors.push("Não podes remover dias de uma inscrição já paga.");
  } else if (oldParticipation.isFullEvent !== true && newParticipation.isFullEvent !== true
    && !isSubset(oldParticipation.daysSelected, newParticipation.daysSelected)) {
    errors.push("Não podes remover dias de uma inscrição já paga.");
  }

  const oldTransfer = previous?.transfer || {};
  const newTransfer = next?.transfer || {};
  if (oldTransfer.enabled === true && newTransfer.enabled !== true) {
    errors.push("Não podes remover o transfer de uma inscrição já paga.");
  }
  if (oldTransfer.enabled === true && String(oldTransfer?.point?.id || "") !== String(newTransfer?.point?.id || "")) {
    errors.push("Não podes trocar o ponto de transfer de uma inscrição já paga.");
  }

  const oldLodging = previous?.lodging || {};
  const newLodging = next?.lodging || {};
  if (oldLodging?.mode === "global") {
    if (newLodging?.mode !== "global" || String(oldLodging?.global?.id || "") !== String(newLodging?.global?.id || "")) {
      errors.push("Não podes remover ou trocar o alojamento de uma inscrição já paga.");
    }
    if (String(oldLodging?.global?.lodgingUnitId || "") !== String(newLodging?.global?.lodgingUnitId || "")) {
      errors.push("Não podes trocar o quarto ou cama de uma inscrição já paga.");
    }
  } else if (oldLodging?.mode === "byDay") {
    const oldDays = oldLodging?.byDay || {};
    const newDays = newLodging?.mode === "byDay" ? newLodging?.byDay || {} : {};
    Object.entries(oldDays).forEach(([day, oldChoice]) => {
      const oldId = typeof oldChoice === "object" ? oldChoice?.id : oldChoice;
      const newChoice = newDays?.[day];
      const newId = typeof newChoice === "object" ? newChoice?.id : newChoice;
      if (String(oldId || "") !== String(newId || "")) {
        errors.push(`Não podes remover ou trocar o alojamento do dia ${Number(day) + 1}.`);
      }
      const oldUnit = oldLodging?.byDayUnits?.[day];
      const newUnit = newLodging?.byDayUnits?.[day];
      if (String(oldUnit || "") !== String(newUnit || "")) {
        errors.push(`Não podes trocar o quarto ou cama do dia ${Number(day) + 1}.`);
      }
    });
  }

  const oldMeals = previous?.meals || {};
  const newMeals = next?.meals || {};
  if (oldMeals?.mode === "global") {
    if (newMeals?.mode !== "global" || !isSubset(ids(oldMeals.global), ids(newMeals.global))) {
      errors.push("Não podes remover refeições de uma inscrição já paga.");
    }
  } else if (oldMeals?.mode === "byDay") {
    Object.entries(oldMeals?.byDay || {}).forEach(([day, oldChoices]) => {
      const nextChoices = newMeals?.mode === "byDay" ? newMeals?.byDay?.[day] : [];
      if (!isSubset(ids(oldChoices), ids(nextChoices))) {
        errors.push(`Não podes remover refeições do dia ${Number(day) + 1}.`);
      }
    });
  }

  Object.entries(oldParticipation?.dayWalkSlotChoice || {}).forEach(([day, oldSlot]) => {
    if (String(oldSlot || "") !== String(newParticipation?.dayWalkSlotChoice?.[day] || "")) {
      errors.push(`Não podes trocar o horário de caminhada do dia ${Number(day) + 1}.`);
    }
  });

  return { valid: errors.length === 0, errors: [...new Set(errors)] };
}

export function registrationPaidTotal(registration = {}) {
  const value = registration?.pricing?.totalBeforeWallet
    ?? registration?.pricing?.baseTotal
    ?? registration?.payment?.amountTotal
    ?? 0;
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.round(number * 100) / 100) : 0;
}
